import { CartaoIndicador, Dica, cn, type TendenciaDoIndicador } from "@base/ui";
import { EXPLICACAO_DO_INDICADOR } from "../domain/explicacoes";
import {
  BadgeDollarSign,
  Hourglass,
  Megaphone,
  PiggyBank,
  Receipt,
  Repeat,
  type LucideIcon,
} from "lucide-react";
import { formatar } from "../domain/formatadores";
import { metaDoIndicador, type IdDoIndicador, type Indicador, type MetasDoPeriodo } from "../domain/indicadores";

const ICONE: Readonly<Record<IdDoIndicador, LucideIcon>> = {
  "faturamento-consulta": BadgeDollarSign,
  "faturamento-tratamento": BadgeDollarSign,
  investimento: Megaphone,
  lucro: PiggyBank,
  "ticket-medio": Receipt,
  roas: Repeat,
  "valor-em-aberto": Hourglass,
};

function DivisaoDoInvestimento({
  plataformas,
}: {
  readonly plataformas: { readonly meta: number; readonly google: number } | null;
}) {
  if (!plataformas) return null;
  return (
    <dl className="mt-3 flex flex-col gap-1 text-xs text-muted-foreground">
      {(
        [
          ["Meta", plataformas.meta],
          ["Google", plataformas.google],
        ] as const
      ).map(([nome, valor]) => (
        <div key={nome} className="flex items-baseline justify-between gap-3">
          <dt>{nome}</dt>
          <dd className="font-medium text-foreground tabular-nums">{formatar.moeda(valor)}</dd>
        </div>
      ))}
    </dl>
  );
}

function tendencia(indicador: Indicador): TendenciaDoIndicador {
  if (indicador.variacao === null || indicador.variacao === 0 || indicador.sentido === "neutro") {
    return "neutra";
  }
  return indicador.variacao > 0 ? "alta" : "baixa";
}

/**
 * Grupo de indicadores financeiros do topo. O Faturamento vem em destaque: e o
 * primeiro numero que o dono da clinica procura.
 */
export function GrupoDeIndicadores({
  indicadores,
  metas = null,
  investimentoPorPlataforma = null,
}: {
  readonly indicadores: readonly Indicador[];
  /** Metas do Funil Projetado para o periodo; `null` sem funil cadastrado. */
  readonly metas?: MetasDoPeriodo | null;
  /** Em reais. So quando Meta e Google estao conectados. */
  readonly investimentoPorPlataforma?: { readonly meta: number; readonly google: number } | null;
}) {
  return (
    <section aria-labelledby="titulo-indicadores" className="flex flex-col gap-bloco">
      <div className="flex flex-col gap-1">
        <h2 id="titulo-indicadores" className="text-xl font-normal tracking-tight">
          Resultado financeiro
        </h2>
        <p className="text-sm text-muted-foreground">Comparado com o período anterior de mesmo tamanho.</p>
      </div>
      <div className="grid grid-cols-1 gap-bloco sm:grid-cols-2 xl:grid-cols-3">
        {indicadores.map((indicador) => {
          const Icone = ICONE[indicador.id];
          return (
            <CartaoIndicador
              key={indicador.id}
              rotulo={indicador.rotulo}
              valor={
                indicador.unidade === "multiplicador"
                  ? formatar.multiplicador(indicador.valor)
                  : formatar.moeda(indicador.valor)
              }
              detalhe={
                indicador.variacao === null
                  ? "Sem base de comparação"
                  : `${formatar.variacao(indicador.variacao)} vs. período anterior`
              }
              tendencia={tendencia(indicador)}
              icone={<Icone />}
              destaque={indicador.id === "faturamento-tratamento"}
              nota={
                indicador.id === "investimento" ? (
                  <DivisaoDoInvestimento plataformas={investimentoPorPlataforma} />
                ) : null
              }
              ajuda={
                <Dica rotulo={`Como ${indicador.rotulo} é calculado`} sobreFundo={indicador.id === "faturamento-tratamento"}>
                  {EXPLICACAO_DO_INDICADOR[indicador.id]}
                </Dica>
              }
            >
              <MetaDoCartao indicador={indicador} meta={metaDoIndicador(indicador.id, metas)} destaque={indicador.id === "faturamento-tratamento"} />
            </CartaoIndicador>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Meta do periodo dentro do cartao: valor da meta e barra do quanto foi
 * atingido. Investimento e "menor ou igual e bom"; o resto, "maior e bom".
 */
function MetaDoCartao({
  indicador,
  meta,
  destaque,
}: {
  readonly indicador: Indicador;
  readonly meta: number | null;
  readonly destaque: boolean;
}) {
  if (meta === null || meta <= 0) return null;
  const formatado = indicador.unidade === "multiplicador" ? formatar.multiplicador(meta) : formatar.moeda(meta);
  const fracao = indicador.valor === null ? null : indicador.valor / meta;
  const bom = fracao !== null && (indicador.id === "investimento" ? fracao <= 1 : fracao >= 1);

  return (
    <div className={cn("mt-5 flex flex-col gap-2 border-t pt-4", destaque ? "border-white/15" : "border-border/70")}>
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className={destaque ? "text-secondary-foreground/75" : "text-muted-foreground"}>
          Meta <strong className={cn("font-semibold tabular-nums", destaque ? "text-secondary-foreground" : "text-foreground")}>{formatado}</strong>
        </span>
        {fracao !== null ? (
          <span className={cn("font-semibold tabular-nums", bom ? (destaque ? "text-ice" : "text-success") : destaque ? "text-alert" : "text-primary")}>
            {formatar.porcentagem(fracao)}
          </span>
        ) : null}
      </div>
      {fracao !== null ? (
        <div
          role="progressbar"
          aria-label={`${indicador.rotulo}: ${formatar.porcentagem(fracao)} da meta`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(Math.min(fracao, 1) * 100)}
          className={cn("h-1.5 overflow-hidden rounded-full", destaque ? "bg-white/15" : "bg-muted")}
        >
          <div
            className={cn("h-full rounded-full transition-all duration-700", bom ? (destaque ? "bg-ice" : "bg-success") : "bg-primary")}
            style={{ width: `${Math.min(fracao, 1) * 100}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}
